/**
 * Plain-language text for a failed delete or archive.
 *
 * The API client throws `new Error(serverMessage)`, so what reaches a screen
 * is whatever the server (or the database underneath it) said: a Postgres
 * "violates foreign key constraint ... on table payments", "API DELETE
 * /vendors/12 failed (409)", or a bare status. People saw that text verbatim.
 * This turns the known shapes into a sentence that says why it failed and what
 * to do about it, and leaves an already-plain message alone.
 */

export interface DeleteErrorContext {
  /** What was being acted on, in the singular: "vendor", "payment". */
  record?: string;
  /** What was being attempted. Defaults to deleting. */
  action?: 'delete' | 'archive' | 'restore';
}

const PAST: Record<string, string> = { delete: 'deleted', archive: 'archived', restore: 'restored' };

const TABLE_NOUNS: Record<string, string> = {
  payments: 'payments',
  vendor_bookings: 'bookings',
  bookings: 'bookings',
  budget_items: 'budget lines',
  tasks: 'tasks',
  guests: 'guests',
  orders: 'orders',
  procurement_orders: 'orders',
  quotes: 'quotes',
  vendor_quotes: 'quotes',
  invoices: 'invoices',
  client_invoices: 'invoices',
  client_quotes: 'quotes',
  events: 'events',
  vehicles: 'vehicles',
  drivers: 'drivers',
  attachments: 'attachments',
  comments: 'comments',
};

const noun = (table: string) => TABLE_NOUNS[table] ?? table.replace(/_/g, ' ');

function statusOf(message: string): number | null {
  const m = message.match(/\((\d{3})\)\s*$/) ?? message.match(/\bstatus (\d{3})\b/i);
  return m ? Number(m[1]) : null;
}

const looksTechnical = (message: string) =>
  /constraint|violates|relation "|syntax error|ECONN|TypeError|undefined|null value|\bSQL\b|API (GET|POST|PUT|PATCH|DELETE)|stack|at \w+\.|\{|\}/i.test(message);

export function friendlyDeleteError(error: unknown, ctx: DeleteErrorContext = {}): string {
  const raw = (error instanceof Error ? error.message : typeof error === 'string' ? error : '').trim();
  const what = ctx.record ? `this ${ctx.record}` : 'this record';
  const status = statusOf(raw);
  const action = ctx.action ?? 'delete';
  const done = PAST[action];

  // Still linked to other records: "still linked to 2 payments" when the
  // server says how many, otherwise the table the database named.
  const counted = raw.match(/(?:referenced|linked|used|attached)\s+(?:by|to)\s+(\d+)\s+([a-z][a-z _-]*?)(?:[.,;]|$)/i);
  if (counted) {
    const n = Number(counted[1]);
    const things = counted[2].trim();
    return `${capitalise(what)} is still linked to ${n} ${things}, so it can't be ${done}. Remove or reassign those first.`;
  }
  const fk = raw.match(/violates foreign key constraint[^]*?(?:on|from) table "?([a-z_]+)"?/i)
    ?? raw.match(/still (?:referenced|linked)[^]*?table "?([a-z_]+)"?/i);
  if (fk) {
    return `${capitalise(what)} is still linked to ${noun(fk[1])}, so it can't be ${done}. Remove or reassign those first, or leave it archived.`;
  }
  if (/violates foreign key constraint|still (?:referenced|linked)|in use/i.test(raw)) {
    return `${capitalise(what)} is still linked to other records, so it can't be ${done}. Remove or reassign those first, or leave it archived.`;
  }

  if (/must be archived|only archived|archive .* first/i.test(raw)) {
    return `Archive ${what} first. Only archived records can be deleted forever.`;
  }
  if (status === 409 && !raw.replace(/\(\d{3}\)/, '').match(/[a-z]{4,}\s+[a-z]{3,}\s+[a-z]{3,}/i)) {
    return `${capitalise(what)} can't be ${done} in its current state. Archive it first, or check what it is linked to.`;
  }
  if (status === 404 || /\bnot found\b|no longer exists/i.test(raw)) {
    return `${capitalise(what)} no longer exists. It may already have been removed. Refresh the page to see the latest.`;
  }
  if (status === 401 || status === 403 || /forbidden|not allowed|permission|unauthori[sz]ed/i.test(raw)) {
    return `You don't have permission to ${action} ${what}. Ask the project owner.`;
  }
  if (/couldn't reach the server|failed to fetch|network|load failed/i.test(raw)) {
    return `Couldn't reach the server, so nothing was ${done}. Check your connection and try again.`;
  }
  if ((status !== null && status >= 500) || raw === '' || looksTechnical(raw)) {
    return `Something went wrong on our side. Nothing was ${done}. Try again in a moment.`;
  }
  return raw;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
